import { useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, Smile, Scissors, Sparkles } from "lucide-react";
import { LuxuryCard } from "@/components/ui/LuxuryCard";
import { useAdaptiveServices, type ServiceId } from "@/hooks/useAdaptiveServices";
import { useGeoAdaptive } from "@/hooks/useGeoAdaptive";
import { trackServicesVariantShown, trackServiceCardClick } from "@/lib/uxTracking";

const SPECIALTY_HREF: Record<ServiceId, string> = {
  vision: "/specialties/vision",
  dental: "/specialties/dental",
  hair: "/specialties/hair",
  aesthetic: "/specialties/aesthetic",
};

interface ServiceCardsProps {
  t: (key: string) => string;
}

const SPOTLIGHT_LABELS: Record<"trust" | "fast" | "neutral", string> = {
  trust: "Recommandé",
  fast: "Disponible",
  neutral: "Populaire",
};

const SERVICE_DEFS: Record<ServiceId, {
  titleKey: string;
  textKey: string;
  featureKeys: [string, string, string];
  Icon: React.FC<{ className?: string }>;
}> = {
  vision:    {
    titleKey: "home.service1Title",
    textKey: "home.service1Text",
    featureKeys: ["home.service1Feature1", "home.service1Feature2", "home.service1Feature3"],
    Icon: Eye,
  },
  dental:    {
    titleKey: "home.service2Title",
    textKey: "home.service2Text",
    featureKeys: ["home.service2Feature1", "home.service2Feature2", "home.service2Feature3"],
    Icon: Smile,
  },
  hair:      {
    titleKey: "home.service3Title",
    textKey: "home.service3Text",
    featureKeys: ["home.service3Feature1", "home.service3Feature2", "home.service3Feature3"],
    Icon: Scissors,
  },
  aesthetic: {
    titleKey: "home.service4Title",
    textKey: "home.service4Text",
    featureKeys: ["home.service4Feature1", "home.service4Feature2", "home.service4Feature3"],
    Icon: Sparkles,
  },
};

/** Static price placeholders per service × currency cluster. */
const PRICE_GUIDE: Record<ServiceId, Record<string, string>> = {
  vision:    { "€": "1 200 €",  "£": "£1,050", "$": "$1,300",  "CA$": "CA$1,750" },
  dental:    { "€": "800 €",    "£": "£680",   "$": "$850",    "CA$": "CA$1,150" },
  hair:      { "€": "1 500 €",  "£": "£1,300", "$": "$1,600",  "CA$": "CA$2,100" },
  aesthetic: { "€": "2 000 €",  "£": "£1,750", "$": "$2,100",  "CA$": "CA$2,800" },
};

const PRICE_LABEL: Record<string, string> = {
  "€": "À partir de", "£": "From", "$": "From", "CA$": "From",
};

export function ServiceCards({ t }: ServiceCardsProps) {
  const { order, spotlight, microcopyVariant, isAdaptive } = useAdaptiveServices();
  const geo = useGeoAdaptive();

  useEffect(() => {
    if (isAdaptive) {
      trackServicesVariantShown(order);
    }
  }, [isAdaptive, order]);

  const variantHash = order.join(",");
  const badgeLabel = microcopyVariant ? SPOTLIGHT_LABELS[microcopyVariant] : SPOTLIGHT_LABELS.neutral;

  return (
    <section
      id="section-specialties"
      className="py-24 bg-card/20"
      data-testid="section-services"
      data-ux-services-variant={variantHash}
    >
      <div className="max-w-6xl mx-auto px-6">
        <motion.h2
          className="text-2xl md:text-3xl font-serif font-bold text-center text-foreground mb-4"
          style={{ fontFamily: "'Playfair Display', serif" }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          data-testid="text-services-title"
        >
          {t("home.servicesTitle")}
        </motion.h2>

        <motion.div
          className="w-12 h-0.5 bg-primary/40 mx-auto mb-14"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {order.map((serviceId, i) => {
            const def = SERVICE_DEFS[serviceId];
            if (!def) return null;
            const { Icon, titleKey, textKey, featureKeys } = def;
            const isSpotlight = spotlight === serviceId;

            const features = featureKeys.map((fk) => t(fk));
            return (
              <motion.div
                key={serviceId}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative"
                data-testid={`card-service-${serviceId}`}
                data-ux-spotlight={isSpotlight ? "true" : undefined}
              >
                {isSpotlight && (
                  <span className="absolute top-3 right-3 z-20 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20 leading-tight select-none">
                    {badgeLabel}
                  </span>
                )}
                <div onClick={() => trackServiceCardClick(serviceId)}>
                  <LuxuryCard
                    icon={<Icon className="w-6 h-6" />}
                    title={t(titleKey)}
                    description={t(textKey)}
                    features={features}
                    href={SPECIALTY_HREF[serviceId]}
                    className={isSpotlight ? "border-primary/25 shadow-md" : ""}
                  />
                </div>
                {geo.isActive && geo.currencySymbol && PRICE_GUIDE[serviceId]?.[geo.currencySymbol] && (
                  <div className="mt-2 pt-3 border-t border-border/20 flex items-center justify-between px-2">
                    <span className="text-[10px] text-muted-foreground/50 uppercase tracking-wide">
                      {geo.priceLabel ?? PRICE_LABEL[geo.currencySymbol] ?? "From"}
                    </span>
                    <span className="text-xs font-medium text-muted-foreground/70">
                      {PRICE_GUIDE[serviceId][geo.currencySymbol]}
                    </span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
